import { useEntity } from "apps/console/context/entity";
import ConsoleApi from "apps/console/enums/consoleApi";
import { useQueryClient } from "react-query";
import { getApiQueryKey, useDeleteApiData, useGetApiData, usePostApiData } from "shared/datalayer";
import { ApiFeaturedListRequest } from "shared/types/ApiFeaturedListRequest";
import { CriteriaConfigType } from "shared/enums/CriteriaConfigType";
import { FeaturedListContextType } from "shared/enums/FeaturedListContextType";
import SelectableType from "shared/enums/SelectableType";
import SelectionType from "shared/enums/SelectionType";
import { useCopies } from "shared/hooks/useCopies";
import ApiField from "shared/types/ApiField";
import ApiAvailableTagsForSelectionFeaturedList from "shared/types/selection/ApiAvailableTagsForSelectionFeaturedList";
import { ApiFeaturedListResponse } from "shared/types/selection/ApiSelectionFeaturedList";
import { ApiSelectionFeaturedListConfig } from "shared/types/selection/ApiSelectionFeaturedListConfig";
import { successToast } from "shared/utils/toastNotification";
import { ApiFeaturedListResponseForListing } from "shared/types/selection/ApiFeaturedListResponseForListing";
import ContextEntityType from "shared/enums/ContextEntityType";

// todo rahil fix query key
function getFSLListQueryKey(programId: string) {
  return getApiQueryKey(ConsoleApi.GET_FEATURED_LIST, { params: { programId } });
}

export function useGetFSLList(
  contextType: FeaturedListContextType,
  contextId: string,
  contextEntityType: ContextEntityType,
  selectionType?: SelectionType
) {
  return useGetApiData<Array<ApiFeaturedListResponseForListing>>(
    ConsoleApi.GET_FEATURED_LIST,
    {
      params: {
        contextId,
        selectionType,
        contextType,
        contextEntityType
      }
    },
    { cacheTime: 0, queryKey: getFSLListQueryKey(contextId) }
  );
}

export function useGetFSLById(id: string) {
  return useGetApiData<ApiFeaturedListResponse>(
    ConsoleApi.GET_FSL_BY_ID,
    { params: { id } },
    { enabled: Boolean(id), cacheTime: 0 }
  );
}

export function useUpdateFsl(programId: string) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  return usePostApiData<ApiFeaturedListResponse, ApiFeaturedListRequest>(ConsoleApi.UPDATE_FSL, {
    onSuccess: () => {
      queryClient.invalidateQueries(getFSLListQueryKey(programId));
      successToast(copies.get("featured.selection.list.updated"));
    }
  });
}

export function useCreateFsl(programId: string) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  return usePostApiData<ApiFeaturedListResponse, ApiFeaturedListRequest>(ConsoleApi.CREATE_FSL, {
    onSuccess: () => {
      queryClient.invalidateQueries(getFSLListQueryKey(programId));
      successToast(copies.get("featured.selection.list.created"));
    }
  });
}

export function useGetSecondaryFieldsList(selectableType: SelectableType) {
  const { registrationId } = useEntity();
  return useGetApiData<Array<ApiField>>(ConsoleApi.GET_SECONDARY_FIELDS_FOR_FSL, {
    params: { registrationId, selectableType }
  });
}

export function useGetFieldsListForMapping() {
  const { registrationId } = useEntity();
  return useGetApiData<Array<ApiField>>(ConsoleApi.GET_FIELDS_FOR_FIELD_MAPPING, {
    params: { registrationId }
  });
}

export function useGetSearchCriteriaForFSLs(type: CriteriaConfigType) {
  const { registrationId } = useEntity();
  return useGetApiData<ApiSelectionFeaturedListConfig>(
    ConsoleApi.FIELDS_LIST_FOR_SEARCH_CRITERIA_ACTOR,
    { params: { registrationId, type } },
    { enabled: Boolean(registrationId) }
  );
}

export function useGetSearchCriteriaForSelectable(type: CriteriaConfigType, selectableType: SelectableType) {
  const { registrationId } = useEntity();
  return useGetApiData<ApiSelectionFeaturedListConfig>(
    ConsoleApi.FIELDS_LIST_FOR_SEARCH_CRITERIA_SELECTABLE,
    { params: { registrationId, type, selectableType } },
    { enabled: Boolean(registrationId) }
  );
}

export function useDeleteFSL(programId: string) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  return useDeleteApiData<void>(ConsoleApi.DELETE_FSL, {
    onSuccess: () => {
      queryClient.invalidateQueries(getFSLListQueryKey(programId));
      successToast(copies.get("featured.selection.list.deleted"));
    }
  });
}

export function useGetAvailableTagsForDefaultFSLs(
  contextType: FeaturedListContextType,
  contextId: string,
  contextEntityType: ContextEntityType,
  selectionType?: SelectionType
) {
  return useGetApiData<ApiAvailableTagsForSelectionFeaturedList>(
    ConsoleApi.AVAILABLE_TAGS_OR_SUB_ROLES_LIST_FOR_DEFAULT_FSL,
    { params: { contextEntityType, contextId, selectionType, contextType } },
    {
      enabled: Boolean(contextId),
      initialData: { availableTags: [], mappedTags: [] } as any
    }
  );
}
